import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SwapVertOutlined } from '@mui/icons-material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import { Box, CircularProgress, Stack, Checkbox, Typography, IconButton } from '@mui/material';
import { ColorsV2 } from 'app/constants/color';
import { Buttons } from 'app/constants/sizing';
import { checkForAssignedChartColor } from 'app/utils';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const ViolenceBarChart = ({ unsortedChartData, loading }) => {
  const DEFAULT_NAVIGATION_STEP = 6;

  const [chartData, setChartData] = useState({});
  const [sortOrder, setSortOrder] = useState('descending'); // This value is overridden immediately on component mount
  const [firstBarInView, setFirstBarInView] = useState(0);
  const [lastBarInView, setlastBarInView] = useState(DEFAULT_NAVIGATION_STEP);
  const [xMax, setXMax] = useState(0);

  const chartColors = useSelector(state => state?.userSettings?.settings?.chartColors) || {};

  const getColor = id => {
    return chartColors[id] || checkForAssignedChartColor(id);
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
    },
    animation: false,
    scales: {
      x: {
        min: 0,
        max: xMax > 100 ? Math.round((xMax + Math.min(xMax * 0.1, 500)) * 0.01) * 100 : 100, // Add a buffer to the max value of the chart scaled with the chart max value
        ticks: {
          color: ColorsV2.white,
        },
      },
      y: {
        ticks: {
          color: ColorsV2.white,
        },
      },
    },
  };

  useEffect(() => {
    // Reset navigation on filter change
    setFirstBarInView(0);
    setlastBarInView(DEFAULT_NAVIGATION_STEP);

    const filteredData = unsortedChartData?.filter(data => data?.deviations > 0 && data?.name); // Filter out data without a valid deviations or name property

    const sortedData = [...(filteredData || [])].sort((a, b) => b?.deviations - a?.deviations);

    const labels = sortedData.map(data => data.name);
    const dataValues = sortedData.map(data => data.deviations);
    const idValues = sortedData.map(data => data.id);

    setXMax(Math.max(...dataValues));

    setSortOrder('ascending');
    setChartData({
      labels,
      datasets: [
        {
          label: 'total count/value',
          data: dataValues,
          backgroundColor: idValues.map(id => getColor(id)),
          borderWidth: 0.5,
          fontSize: 14,
        },
      ],
    });
  }, [unsortedChartData]);

  // Handle sorting of high-to-low or low-to-high
  const handleSort = () => {
    if (!Object.keys(chartData)?.length) return;

    // Reverse data and labels
    // Data is always sorted on initialisation and are never updated, thus we can simply reverse it without needing to re-sort
    const reversedData = [...(chartData.datasets?.[0]?.data || [])].reverse();
    const reversedLabels = [...(chartData.labels || [])].reverse();

    // Reverse colors in a way that respects label -> color consistency across graphs
    const reversedColors = reversedLabels.map(label => getColor(label));

    // Toggle sort order
    setSortOrder(sortOrder === 'ascending' ? 'descending' : 'ascending');

    setChartData(prevData => ({
      ...prevData,
      labels: reversedLabels,
      datasets: [
        {
          ...prevData.datasets?.[0],
          data: reversedData,
          backgroundColor: reversedColors,
        },
      ],
    }));
  };

  const handleNavigation = direction => {
    let step = 0;

    // Check for chart bounds and make sure we don't navigate further than the chart allows
    if (direction === -1 && firstBarInView - DEFAULT_NAVIGATION_STEP < 0) {
      step = firstBarInView;
    } else if (direction === 1 && lastBarInView + DEFAULT_NAVIGATION_STEP >= chartData?.labels?.length) {
      step =
        lastBarInView >= chartData?.labels?.length ? 0 : chartData?.labels?.length - lastBarInView;
    } else {
      step = DEFAULT_NAVIGATION_STEP;
    }
    setlastBarInView(prev => prev + step * direction);
    setFirstBarInView(prev => prev + step * direction);
  };

  const { t } = useTranslation('translation', { keyPrefix: 'compliance' });

  if (loading || !Object.keys(chartData)?.length) {
    return (
      <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <CircularProgress size='4rem' sx={{ color: ColorsV2.acent }} />
      </Box>
    );
  }
  return (
    <>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' justifyContent='center'>
          <Checkbox
            icon={<SwapVertOutlined></SwapVertOutlined>}
            checkedIcon={<SwapVertOutlined></SwapVertOutlined>}
            disableRipple
            onChange={handleSort}
            size='small'
            sx={{
              color: ColorsV2.acent,
              margin: '5px',
              marginTop: '10px',
              marginBottom: '10px',
              '&.Mui-checked': {
                color: ColorsV2.acent,
              },
              '&:hover': {
                color: ColorsV2.white,
                bgcolor: ColorsV2.acent,
              },
            }}
          ></Checkbox>
          <Typography
            variant='caption'
            sx={{
              color: ColorsV2.textInfo,
            }}
          >
            {t('sorting')}{' '}
            {sortOrder === 'ascending' ? t('sort_high_to_low') : t('sort_low_to_high')}
          </Typography>
        </Stack>
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='center'
          sx={{ marginRight: '10px' }}
        >
          <IconButton
            onClick={() => handleNavigation(-1)}
            sx={{
              ...Buttons.baseStyle.iconButton,
            }}
            disabled={firstBarInView <= 0}
          >
            <KeyboardArrowUpOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleNavigation(1)}
            sx={{
              ...Buttons.baseStyle.iconButton,
            }}
            disabled={lastBarInView >= (chartData?.labels?.length || 0)}
          >
            <KeyboardArrowDownOutlinedIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Bar
        options={options}
        data={{
          ...chartData,
          labels: chartData.labels.slice(firstBarInView, lastBarInView),
          datasets: [
            {
              ...chartData.datasets?.[0],
              data: chartData.datasets?.[0].data.slice(firstBarInView, lastBarInView),
              backgroundColor: chartData.datasets?.[0].backgroundColor.slice(
                firstBarInView,
                lastBarInView
              ),
            },
          ],
        }}
        height={80}
      />
    </>
  );
};

export default React.memo(ViolenceBarChart);
